'use client';

import { useTranslations } from 'next-intl';
import { ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { MeasurementSystem } from '../api/estimate-service';

interface EstimateTitleTagsProps {
  /** Current display currency (3-letter ISO, uppercase). */
  currency: string;
  measurementSystem: MeasurementSystem;
  disabled?: boolean;
  /** Open the independent currency picker dialog (EstimateCurrencySheet —
   *  hosted by the editor). */
  onOpenCurrencyPicker: () => void;
  isChangingCurrency?: boolean;
  /** Instant metric ↔ imperial toggle (server converts every value). */
  onMeasurementToggle: () => void;
  isChangingMeasurement?: boolean;
}

const TAG_CLASS =
  'inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 ' +
  'text-chip text-primary-foreground whitespace-nowrap transition-colors ' +
  'hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50';

/**
 * Display-denomination tag-toggles next to the "Cost estimate" title: the
 * currency tag opens the currency picker, the units tag instantly flips
 * metric ↔ imperial. Both are DISPLAY switches (the server converts every
 * amount, storage stays USD/metric) — which is why they live on the document
 * header rather than in the settings list: the settings list holds generation
 * inputs that only affect the next regenerate.
 */
export function EstimateTitleTags({
  currency,
  measurementSystem,
  disabled = false,
  onOpenCurrencyPicker,
  isChangingCurrency = false,
  onMeasurementToggle,
  isChangingMeasurement = false,
}: EstimateTitleTagsProps) {
  const t = useTranslations('common.estimate.editor');
  const tMeasurement = useTranslations('common.estimate.measurement');

  const measurementLabel =
    measurementSystem === 'imperial'
      ? tMeasurement('imperial')
      : tMeasurement('metric');

  return (
    <>
      <button
        type="button"
        onClick={onOpenCurrencyPicker}
        disabled={disabled || isChangingCurrency}
        aria-label={t('selectCurrency')}
        className={cn(TAG_CLASS, 'uppercase')}
      >
        {isChangingCurrency && (
          <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
        )}
        {currency}
        {/* Switcher chevron — the affordance that the tag is an interactive
            value switch, not a status badge (Apple/Android parity). */}
        <ChevronsUpDown className="h-3.5 w-3.5" strokeWidth={2.5} />
      </button>

      <button
        type="button"
        onClick={onMeasurementToggle}
        disabled={disabled || isChangingMeasurement}
        aria-label={t('toggleMeasurement')}
        className={TAG_CLASS}
      >
        {isChangingMeasurement && (
          <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
        )}
        {measurementLabel}
        <ChevronsUpDown className="h-3.5 w-3.5" strokeWidth={2.5} />
      </button>
    </>
  );
}
